import React from 'react';
import { Typography, Box, Button, useMediaQuery } from '@mui/material';
import { getText, formatCardNumber, trimCardForMobile, currencyNetf } from '../../libs/utils';
import { OrderTransactopn } from "../../assets/images/icons";
import { styled, useTheme } from '@mui/material/styles';
import NetworkLoad from '../../components/NetworkLoad';

const OrderData = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '25px',
  alignItems: 'start',
  margin: '30px 0 0',
  width: '100%',
  flexWrap: 'wrap',
  [theme.breakpoints.only('md')]: {
    gap: '12px 25px'
  },
  [theme.breakpoints.only('xs')]: {
    gap: '12px 25px'
  },
  " > .MuiBox-root": {

    "&.breakline": {
      display: 'none',
      flexBasis: '100%',
      height: 0,
      [theme.breakpoints.only('md')]: {
        display: 'block'
      },
      [theme.breakpoints.only('xs')]: {
        display: 'block'
      }
    }
  },
  ":empty": {
    display: 'none'
  }
}))

const OrderButtons = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '10px',
  alignItems: 'start',
  margin: '30px 0 0',
  width: '100%',
  flexWrap: 'wrap',
  " > .MuiBox-root": {
    paddingRight: '15px',
  },
  ":empty": {
    display: 'none'
  }
}))

const GreyBox = styled(Box)(({ theme }) => ({
  background: 'rgba(169, 169, 169, 0.05)',
  borderRadius: '11px',
  height: '100%',
  justifyContent: 'center',
  display: 'flex',
  flexFlow: 'column',
  alignItems: 'center',
  minHeight: '300px',
  flex: 1,
  marginTop: '30px',
}));

const OrderProcessing = ({ order }) => {
  const theme = useTheme();
  let isXS = useMediaQuery(theme.breakpoints.only('xs'));

  let reqsList = [];
  if (order.paymentStatus.id == 6) {
    reqsList = order.clientDetails.filter(field => field.name?.startsWith('to[')).map(field => <Box key={field.name}>
      <Box sx={{ fontWeight: 600, wordBreak: 'break-all' }}>
        {field.name === 'to[number]' ? formatCardNumber(field.value, order.toPaymentSystemType?.value) : field.value}
      </Box>
      <Box sx={{ color: 'primary.grey', fontSize: 12 }}>{field.label}</Box>
    </Box>)
  }

  if (order.depositViewType && order.depositViewType.value != "PAYSYSTEM" && order.depositViewType.value != "BANK" && [7, 70, 71].includes(order.paymentStatus.id) && order.deposits[0]) {
    reqsList = order.deposits[0].tabs[0].outputs.map((reqs, key) => {
      if (reqs.type == "text") {
        return <React.Fragment key={key}>
          <Box>
            <Box sx={{ fontWeight: 600, wordBreak: 'break-all' }}>
              {isXS && reqs.name === 'number' ? trimCardForMobile(reqs.value) : reqs.value}
            </Box>
            <Box sx={{ color: 'primary.grey', fontSize: 12 }}>{reqs.label}</Box>
          </Box>
          {key == 1 && <Box className='breakline'></Box>}
        </React.Fragment>
      }
    })
  }

  let titles = {
    7: getText("orderSBStage2"),
    70: getText("orderSBStage2"),
    71: getText("orderSBStage2"),
    6: getText("orderSBStage4"),
  }
  let subTitles = {
    7: getText("orderProcessSubhedS7"),
    70: getText("orderProcessSubhedS70"),
    71: getText("orderProcessSubhedS71"),
    6: getText("orderProcessSubhedS6"),
  }



  if (order.depositViewType.value === "CRYPT") {
    titles = {
      7: getText("orderHeaderStateHasTransWait"),
      70: getText("orderHeaderStateHasTransWait"),
      71: getText("orderHeaderStateTransWait"),
      6: getText("orderSBStage4"),
    }
    subTitles = {
      7: getText("orderProcessSubhedS7"),
      70: getText("orderProcessSubhedS70"),
      71: getText("orderProcessSubhedS71"),
      6: getText("orderProcessSubhedS6"),
    }
  }

  if (order.toPaymentSystemType?.value === 'CHECK') {
    subTitles = {
      ...subTitles,
      6: getText("orderProcessSubhedS6Check"),
    }
  }

  if (order.depositViewType.value === 'CHECK') {
    subTitles = {
      ...subTitles,
      7: getText('orderProcessSubhedS7Check')
    }
  }

  if (order.depositViewType.value === 'BANK') {
    subTitles = {
      ...subTitles,
      7: getText('orderProcessSubhedS7Bank')
    } 
  }


  const networkInfo = order.fromNetworkInfo;

  return (
    <Box sx={order.toPaymentSystemType?.value == 'EXTERNAL' ? { display: 'flex', flexDirection: 'column', justifyContent: 'start', height: '100%' } : {}}>

      <Typography variant="h3">{titles[order.paymentStatus.id]}</Typography>
      <Typography variant="regularCaption">{subTitles[order.paymentStatus.id]}</Typography>

      {reqsList.length > 0 && <OrderData>
        {reqsList}

        {order.paymentStatus.id != 6 && order.depositViewType.value == "CRYPT" && <>
          {networkInfo?.load &&
            <NetworkLoad order={order} />
          }

          {networkInfo?.recommendedFee &&
            <Box>
              <Box sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: 14 }}>{networkInfo.recommendedFee}</Box>
              <Box sx={{ color: 'primary.grey', fontSize: 12 }}>{getText("networkRecomendedFee")}</Box>
            </Box>
          }
        </>}

      </OrderData>}

      <OrderButtons>
        {order.transactionLink && [7, 70].includes(order.paymentStatus.id) &&
          <Button
            size="medium"
            sx={{ padding: '17px 39px', width: { xs: '100%', sm: 'auto' } }}
            startIcon={<OrderTransactopn />}
            onClick={() => window.open(order.transactionLink, '_blank')}
            className="basicIconLeft"
          >
            {getText('trnsCheckButton')}
          </Button>
        }
      </OrderButtons>

      <GreyBox>
        <span className="dot-spin"></span>
        <Typography sx={{ display: 'block', fontSize: '12px', textAlign: 'center' }}>
          {getText('ocrPlsWait')} <span style={{ textTransform: 'lowercase' }}>{titles[order.paymentStatus.id]}</span>
          {order.toPaymentSystemType?.value === "BANK" && order.paymentStatus.id === 6 && <div style={{ marginTop: '8px' }}>{getText("orderSBChatConfirm")}</div>}
        </Typography>
      </GreyBox>
    </Box>
  )
}

export default OrderProcessing